import { useForm } from "react-hook-form";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Mail, ArrowRight, User, Briefcase, Activity, ShieldCheck, Building2, ChevronLeft } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ErrorModal } from "@/components/ui/error-modal";
import { API_BASE } from "@/lib/api-client-react";
import { cn } from "@/lib/utils";

type RoleType = "member" | "agent" | "provider";

type EmailFormData = { email: string };
type OTPFormData = { otp: string };

export default function Login() {
  const [, setLocation] = useLocation();
  const [role, setRole] = useState<RoleType>("member");
  const [step, setStep] = useState<0 | 1>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const emailForm = useForm<EmailFormData>({
    defaultValues: { email: "" },
    mode: "onSubmit",
  });

  const otpForm = useForm<OTPFormData>({
    defaultValues: { otp: "" },
    mode: "onChange",
  });

  const onEmailSubmit = async (data: EmailFormData) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email, type: "login", role }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setStep(1);
    } catch (err: any) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const [localityModalOpen, setLocalityModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<1 | 2>(1);
  const [localitiesList, setLocalitiesList] = useState<any[]>([]);
  const [selectedLocalityIds, setSelectedLocalityIds] = useState<string[]>([]);
  const [categoryRates, setCategoryRates] = useState<Record<string, Record<string, string>>>({});
  const [batchRateInput, setBatchRateInput] = useState<string>("110");
  const [savingLocalities, setSavingLocalities] = useState(false);
  const [selectedStateFilter, setSelectedStateFilter] = useState<string>("ALL");

  const CPT_CATEGORIES = [
    "Evaluation & Management",
    "Surgery",
    "Radiology",
    "Pathology",
    "Medicine",
    "Physical Therapy",
    "Anesthesia",
    "Diagnostic Cardiology",
    "General",
    "Dentistry"
  ];

  const uniqueStates = Array.from(new Set(localitiesList.map((l: any) => l.state).filter(Boolean))).sort();
  const filteredLocalities = selectedStateFilter === "ALL" ? localitiesList : localitiesList.filter((l: any) => l.state === selectedStateFilter);

  const fetchLocalities = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/localities`);
      const json = await res.json();
      if (json.success) setLocalitiesList(json.data || []);
    } catch {}
  };

  const onOTPSubmit = async (data: OTPFormData) => {
    setLoading(true);
    try {
      const email = emailForm.getValues("email");
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: data.otp, role }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      const returnedRole = json.data.user.role;
      const returnedPracticeType = json.data.user.provider?.practiceType;

      localStorage.setItem("token", json.data.token);
      localStorage.setItem("user", JSON.stringify(json.data.user));
      if (returnedPracticeType) {
        localStorage.setItem("practiceType", returnedPracticeType);
      }

      if (role === "provider") setLocation("/provider/dashboard");
      else if (role === "agent") setLocation("/agent/dashboard");
      else setLocation("/member/dashboard");

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyBatchRateToAll = () => {
    const rate = batchRateInput || "110";
    const newRates: Record<string, Record<string, string>> = {};
    selectedLocalityIds.forEach(locId => {
      newRates[locId] = {};
      CPT_CATEGORIES.forEach(cat => {
        newRates[locId][cat] = rate;
      });
    });
    setCategoryRates(newRates);
  };

  const handleSaveLocalities = async () => {
    if (selectedLocalityIds.length === 0) {
      return alert("Please select at least one locality to proceed.");
    }
    setSavingLocalities(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/auth/update-localities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          localityIds: selectedLocalityIds,
          categoryRates,
          defaultMedicareRate: Number(batchRateInput) || 110
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to update localities");

      localStorage.setItem("user", JSON.stringify(json.data));
      setLocalityModalOpen(false);

      if (role === "provider") setLocation("/provider/dashboard");
      else if (role === "agent") setLocation("/agent/dashboard");
      else setLocation("/member/dashboard");
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSavingLocalities(false);
    }
  };


  const roles = [
    { id: "member", label: "Member", icon: User, desc: "Manage your health" },
    { id: "agent", label: "Agent", icon: Briefcase, desc: "Support members" },
    { id: "provider", label: "Provider", icon: Activity, desc: "Manage patients" },
  ] as const;

  return (
    <>
      <div className="min-h-screen grid lg:grid-cols-2">
        {/* Left — Brand Panel */}
        <div className="hidden lg:flex flex-col bg-gradient-to-br from-primary via-primary/90 to-secondary p-12 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.08),transparent_60%)]" />
          <div className="flex items-center gap-3 relative z-10">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl">StraightDoc</span>
          </div>
          <div className="flex-1 flex flex-col justify-center relative z-10">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <h1 className="text-4xl font-bold mb-4 leading-tight">Welcome back to your health hub</h1>
              <p className="text-white/70 text-lg leading-relaxed mb-10">Access your care team, upcoming appointments, and cost estimates — all in one place.</p>
            </motion.div>
            <div className="space-y-4">
              {["Passwordless secure login", "Find and book top-rated providers", "Coordinate your entire care team", "HIPAA compliant"].map((item, i) => (
                <motion.div key={item} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="flex items-center gap-3 text-white/90">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — Form */}
        <div className="flex flex-col p-6 sm:p-10 bg-background relative overflow-y-auto">
          <div className="w-full max-w-xl mx-auto relative z-10 my-auto pt-4 pb-12">
            <div className="flex items-center gap-2 mb-8 lg:hidden">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">StraightDoc</span>
            </div>

            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="step0"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="mb-2">
                    <h2 className="text-2xl font-bold mb-2">Sign in to your account</h2>
                    <p className="text-muted-foreground mb-6">
                      Don't have an account? <Link href="/register" className="text-primary hover:underline font-medium">Create one free</Link>
                    </p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium flex items-start gap-2">
                      <ShieldCheck className="w-5 h-5 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <div>
                    <Label className="mb-3 block text-muted-foreground">I am logging in as a:</Label>
                    <div className="grid grid-cols-3 gap-3 mb-8">
                      {roles.map(r => {
                        const Icon = r.icon;
                        const isActive = role === r.id;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => setRole(r.id)}
                            className={`relative p-4 rounded-xl border text-left transition-all ${isActive
                                ? "border-primary bg-primary/5 ring-1 ring-primary"
                                : "border-border hover:border-primary/30 hover:bg-muted/50"
                              }`}
                          >
                            <Icon className={`w-5 h-5 mb-3 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                            <div className={`font-semibold text-sm ${isActive ? "text-foreground" : "text-muted-foreground"}`}>{r.label}</div>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <Form {...emailForm}>
                    <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-4">
                      <FormField
                        control={emailForm.control}
                        name="email"
                        rules={{
                          required: "Email is required",
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <Label>Email</Label>
                            <FormControl>
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input {...field} type="email" placeholder="you@example.com" className="pl-10 h-11" />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="submit"
                        className="w-full h-11 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white border-0 rounded-xl font-semibold mt-2"
                        disabled={loading || !emailForm.formState.isValid}
                      >
                        {loading ? "Sending code..." : <span className="flex items-center gap-2">Continue <ArrowRight className="w-4 h-4" /></span>}
                      </Button>
                    </form>
                  </Form>
                  <p className="text-center text-xs text-muted-foreground mt-4">
                    By signing in, you agree to our{" "}
                    <a href="#" className="text-primary hover:underline">Terms of Service</a> and{" "}
                    <a href="#" className="text-primary hover:underline">Privacy Policy</a>.
                  </p>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="mb-8">
                    <button onClick={() => setStep(0)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4 transition-colors">
                      &larr; Back
                    </button>
                    <h2 className="text-2xl font-bold mb-2">Check your email</h2>
                    <p className="text-muted-foreground">
                      We sent a 6-digit verification code to <span className="font-medium text-foreground">{emailForm.getValues("email")}</span>.
                    </p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium flex items-start gap-2">
                      <ShieldCheck className="w-5 h-5 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <Form {...otpForm}>
                    <form onSubmit={otpForm.handleSubmit(onOTPSubmit)} className="space-y-6">
                      <FormField
                        control={otpForm.control}
                        name="otp"
                        rules={{
                          required: "Verification code is required",
                          minLength: { value: 6, message: "Code must be 6 digits" },
                        }}
                        render={({ field }) => (
                          <FormItem className="flex flex-col items-center">
                            <FormControl>
                              <InputOTP maxLength={6} autoFocus {...field}>
                                <InputOTPGroup>
                                  <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
                                </InputOTPGroup>
                              </InputOTP>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="submit"
                        className="w-full h-11 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white border-0 rounded-xl font-semibold"
                        disabled={loading || otpForm.watch("otp")?.length !== 6}
                      >
                        {loading ? "Verifying..." : "Verify Code & Sign In"}
                      </Button>
                      <p className="text-center text-sm text-muted-foreground">
                        Didn't receive the code? <button type="button" className="text-primary hover:underline font-medium">Resend</button>
                      </p>
                    </form>
                  </Form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ── MANDATORY LOCALITY & CATEGORY PERCENTAGE SELECTION MODAL ── */}
      {localityModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-2xl bg-white rounded-2xl p-6 shadow-2xl space-y-4 border border-slate-100 my-auto">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
              <div className="p-2.5 bg-primary/10 text-primary rounded-xl">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  {modalStep === 1 ? "Step 1: Locality Selection Required" : "Step 2: Category & Locality Fee Schedule Percentages (%)"}
                </h3>
                <p className="text-xs text-slate-500">
                  {modalStep === 1
                    ? "Please select one or more localities to continue."
                    : "Specify your Fee Schedule percentage rate (%) locality-wise and category-wise."}
                </p>
              </div>
            </div>

            {modalStep === 1 ? (
              <>
                {/* State Filter Pills */}
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Filter Localities by State</label>
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs no-scrollbar">
                    <button
                      type="button"
                      onClick={() => setSelectedStateFilter("ALL")}
                      className={cn(
                        "px-3 py-1 rounded-full border text-xs transition-all flex-shrink-0 font-medium",
                        selectedStateFilter === "ALL"
                          ? "bg-primary text-white border-primary shadow-xs"
                          : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                      )}
                    >
                      All States ({localitiesList.length})
                    </button>
                    {uniqueStates.map((st: any) => {
                      const count = localitiesList.filter(l => l.state === st).length;
                      return (
                        <button
                          key={st}
                          type="button"
                          onClick={() => setSelectedStateFilter(st)}
                          className={cn(
                            "px-3 py-1 rounded-full border text-xs transition-all flex-shrink-0 font-medium",
                            selectedStateFilter === st
                              ? "bg-primary text-white border-primary shadow-xs"
                              : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                          )}
                        >
                          {st} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Localities List */}
                <div className="space-y-2 max-h-60 overflow-y-auto p-1">
                  {filteredLocalities.map((loc) => {
                    const isSelected = selectedLocalityIds.includes(loc.id);
                    return (
                      <button
                        key={loc.id}
                        type="button"
                        onClick={() => {
                          setSelectedLocalityIds((prev) =>
                            isSelected ? prev.filter((id) => id !== loc.id) : [...prev, loc.id]
                          );
                        }}
                        className={cn(
                          "w-full text-left p-3 rounded-xl border text-xs font-medium flex items-center justify-between transition-all",
                          isSelected
                            ? "border-primary bg-primary/5 text-primary shadow-xs"
                            : "border-slate-200 text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div>
                          <div className="font-semibold text-sm">{loc.name} {loc.localityNumber ? `(#${loc.localityNumber})` : ''}</div>
                          <div className="text-slate-500 text-[11px]">
                            {[loc.city, loc.state, loc.zipCode].filter(Boolean).join(", ")}
                          </div>
                        </div>
                        {isSelected && <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />}
                      </button>
                    );
                  })}
                  {filteredLocalities.length === 0 && (
                    <div className="text-center py-6 text-slate-400 text-xs">
                      No localities found for the selected state filter.
                    </div>
                  )}
                </div>

                <div className="pt-2 flex justify-end">
                  <Button
                    type="button"
                    onClick={() => {
                      if (selectedLocalityIds.length === 0) {
                        return alert("Please select at least one locality.");
                      }
                      handleApplyBatchRateToAll();
                      setModalStep(2);
                    }}
                    disabled={selectedLocalityIds.length === 0}
                    className="w-full h-10 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90"
                  >
                    Next: Set Fee Schedule Percentages →
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Batch Rate Quick Apply */}
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-700">Quick Fill All Categories:</span>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={batchRateInput}
                        onChange={(e) => setBatchRateInput(e.target.value)}
                        className="w-20 h-8 text-xs font-bold text-center rounded-lg bg-white"
                        placeholder="110"
                      />
                      <span className="text-xs font-bold text-slate-600">%</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleApplyBatchRateToAll}
                    className="h-8 text-xs font-semibold rounded-lg bg-white hover:bg-primary/5 hover:text-primary"
                  >
                    Apply to All
                  </Button>
                </div>

                {/* Per Locality & Category Rate Fields */}
                <div className="space-y-4 max-h-72 overflow-y-auto p-1 pr-2">
                  {selectedLocalityIds.map((locId) => {
                    const locObj = localitiesList.find((l) => l.id === locId);
                    return (
                      <div key={locId} className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white shadow-xs">
                        <div className="font-bold text-xs text-primary uppercase tracking-wider flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Locality: {locObj ? `${locObj.state ? locObj.state + ' - ' : ''}${locObj.name} (#${locObj.localityNumber || locObj.id})` : locId}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          {CPT_CATEGORIES.map((cat) => {
                            const val = categoryRates[locId]?.[cat] ?? batchRateInput ?? "110";
                            return (
                              <div key={cat} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-150 text-xs">
                                <span className="font-medium text-slate-700 truncate mr-2" title={cat}>{cat}</span>
                                <div className="flex items-center gap-1 shrink-0">
                                  <input
                                    type="number"
                                    value={val}
                                    onChange={(e) => {
                                      const newVal = e.target.value;
                                      setCategoryRates((prev) => ({
                                        ...prev,
                                        [locId]: {
                                          ...(prev[locId] || {}),
                                          [cat]: newVal
                                        }
                                      }));
                                    }}
                                    className="w-16 h-7 text-xs font-bold text-center rounded border border-slate-300 bg-white"
                                  />
                                  <span className="font-bold text-slate-500">%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="pt-2 flex items-center justify-between gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setModalStep(1)}
                    className="h-10 px-4 rounded-xl text-xs font-semibold"
                  >
                    ← Back to Localities
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSaveLocalities}
                    disabled={savingLocalities}
                    className="h-10 px-6 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 text-xs"
                  >
                    {savingLocalities ? "Saving..." : "Save & Complete Setup"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
